import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Check, ChevronDown, GitBranch, Loader2, Plus } from 'lucide-react';
import AddBranchDialog from './AddBranchDialog';
import { cn } from '../../lib/utils';
import { isMobileContainer } from '../../utils/device';
import { isSchoolFeatureEnabled } from '../../utils/schoolFeatures';

function sortBranches(list) {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const aPrimary = Number(a?.is_primary) === 1 ? 1 : 0;
    const bPrimary = Number(b?.is_primary) === 1 ? 1 : 0;
    if (aPrimary !== bPrimary) return bPrimary - aPrimary;
    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' });
  });
}

function dedupeBranches(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return sortBranches(list.filter((branch) => {
    const id = String(branch?.id ?? '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  }));
}

function BranchMenuItems({
  hasBranches,
  branches,
  activeBranchId,
  onSwitch,
  onAdd,
  className = '',
}) {
  const itemClass = 'flex w-full items-center gap-2 rounded-lg px-3 py-3 text-left text-sm hover:bg-accent active:bg-accent';

  return (
    <div className={cn('space-y-1', className)}>
      {hasBranches ? (
        <>
          <button type="button" className={itemClass} onClick={() => onSwitch(null)}>
            <span className="flex-1">All branches</span>
            {activeBranchId == null ? <Check className="h-4 w-4 text-blue-500 shrink-0" /> : null}
          </button>
          {branches.map((branch) => (
            <button
              key={branch.id}
              type="button"
              className={itemClass}
              onClick={() => onSwitch(branch.id)}
            >
              <span className="flex-1 truncate">{branch.name}</span>
              {String(activeBranchId) === String(branch.id) ? (
                <Check className="h-4 w-4 text-blue-500 shrink-0" />
              ) : null}
            </button>
          ))}
          <div className="my-2 border-t border-border" />
        </>
      ) : null}
      <button
        type="button"
        className={cn(itemClass, 'text-blue-600')}
        onClick={onAdd}
      >
        <Plus className="h-4 w-4 shrink-0" />
        <span>Add branch</span>
      </button>
    </div>
  );
}

export default function BranchPickerMenu({
  variant = 'settings',
  align = 'start',
  className,
}) {
  const { user, switchBranch } = useAuth();
  const { toast } = useToast();
  const [branches, setBranches] = useState(() => dedupeBranches(user?.available_branches || []));
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const activeBranchId = user?.active_branch_id ?? null;
  const hasBranches = branches.length > 0;
  const isHeaderMobile = variant === 'mobile';
  const isDashboardMobile = variant === 'dashboard' && isMobileContainer();
  const useSheet = isMobileContainer() && (isHeaderMobile || isDashboardMobile);

  const activeLabel = useMemo(() => {
    if (switching) return 'Switching…';
    if (user?.active_branch_name) return user.active_branch_name;
    if (!hasBranches) return 'Add branch';
    return 'All branches';
  }, [switching, user?.active_branch_name, hasBranches]);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/branches');
      const body = res.data;
      const list = body?.data ?? body;
      setBranches(dedupeBranches(list));
    } catch {
      setBranches(dedupeBranches(user?.available_branches || []));
    } finally {
      setLoading(false);
    }
  }, [user?.available_branches]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleSwitch = async (branchId) => {
    const current = activeBranchId != null ? String(activeBranchId) : '';
    const next = branchId != null ? String(branchId) : '';
    if (current === next) {
      setSheetOpen(false);
      return;
    }

    setSwitching(true);
    try {
      const result = await switchBranch(branchId);
      if (!result?.success) {
        toast({
          title: 'Could not switch branch',
          description: result?.message || 'Please try again.',
          variant: 'destructive',
        });
        return;
      }
      const name = branchId
        ? branches.find((b) => String(b.id) === String(branchId))?.name || 'Branch'
        : 'All branches';
      toast({ title: 'Branch switched', description: `Now viewing ${name}.` });
      setSheetOpen(false);
      window.location.reload();
    } finally {
      setSwitching(false);
    }
  };

  const handleBranchCreated = async (branch) => {
    await fetchBranches();
    if (branch?.id) {
      await handleSwitch(branch.id);
    }
  };

  const openAddDialog = () => {
    setSheetOpen(false);
    setAddOpen(true);
  };

  if (user?.role_slug !== 'school_admin') {
    return null;
  }

  if (!isSchoolFeatureEnabled(user, 'branches')) {
    return null;
  }

  const menuProps = {
    hasBranches,
    branches,
    activeBranchId,
    onSwitch: handleSwitch,
    onAdd: openAddDialog,
  };

  if (useSheet) {
    const showActiveDot = isHeaderMobile && activeBranchId != null;
    const sheetTrigger = isDashboardMobile ? (
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between rounded-lg font-normal h-10"
        disabled={switching || loading}
        onClick={() => setSheetOpen(true)}
      >
        <span className="flex items-center gap-2 truncate min-w-0">
          <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm">{activeLabel}</span>
        </span>
        {switching || loading ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        )}
      </Button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent"
        disabled={switching || loading}
        title={activeLabel}
        aria-label={`Branch: ${activeLabel}`}
        onClick={() => setSheetOpen(true)}
      >
        {switching || loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <GitBranch className="h-5 w-5" />
        )}
        {showActiveDot ? (
          <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-background" />
        ) : null}
      </Button>
    );

    return (
      <div className={className}>
        {sheetTrigger}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl pb-safe max-h-[70vh] overflow-y-auto">
            <SheetHeader className="text-left pb-2">
              <SheetTitle>Switch branch</SheetTitle>
              <p className="text-sm text-muted-foreground font-normal">
                {activeLabel === 'Add branch' ? 'Create your first campus branch' : `Current: ${activeLabel}`}
              </p>
            </SheetHeader>
            <BranchMenuItems {...menuProps} />
          </SheetContent>
        </Sheet>
        <AddBranchDialog open={addOpen} onOpenChange={setAddOpen} onCreated={handleBranchCreated} />
      </div>
    );
  }

  const isCompact = variant === 'compact';
  const isDashboard = variant === 'dashboard';
  const triggerClassName = isCompact
    ? 'h-9 max-w-[160px] gap-1.5 border-border text-foreground font-normal'
    : cn(
        'w-full justify-between rounded-lg font-normal',
        isDashboard ? '' : 'mt-1.5'
      );

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={isCompact ? 'sm' : 'default'}
            className={cn('justify-between', triggerClassName)}
            disabled={switching || loading}
          >
            <span className="flex items-center gap-2 truncate min-w-0">
              <GitBranch className={cn('shrink-0 text-muted-foreground', isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
              <span className={cn('truncate', isCompact && 'text-xs font-medium')}>{activeLabel}</span>
            </span>
            {switching || loading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className={isCompact ? 'w-56' : 'w-[var(--radix-dropdown-menu-trigger-width)]'}
        >
          {hasBranches ? (
            <>
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={(e) => {
                  e.preventDefault();
                  handleSwitch(null);
                }}
              >
                <span className="flex-1">All branches</span>
                {activeBranchId == null ? <Check className="h-4 w-4 text-blue-500" /> : null}
              </DropdownMenuItem>
              {branches.map((branch) => (
                <DropdownMenuItem
                  key={branch.id}
                  className="cursor-pointer"
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSwitch(branch.id);
                  }}
                >
                  <span className="flex-1 truncate">{branch.name}</span>
                  {String(activeBranchId) === String(branch.id) ? (
                    <Check className="h-4 w-4 text-blue-500 shrink-0" />
                  ) : null}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuItem
            className="cursor-pointer text-blue-600 focus:text-blue-600"
            onSelect={(e) => {
              e.preventDefault();
              setAddOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add branch
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AddBranchDialog open={addOpen} onOpenChange={setAddOpen} onCreated={handleBranchCreated} />
    </div>
  );
}
